import React from "react";
import { Link } from "react-router-dom";
import { IconCorporateFareBlue } from "assets/svg";
import type { SellerPublicCard } from "reducers/companyProfileApi";
import ProductSellerBadgeList from "./ProductSellerBadgeList";
import {
  formatCount,
  getPublicAssetUrl,
} from "./productSellerCompanyCard.utils";

interface ProductSellerCompanySummaryProps {
  card: SellerPublicCard;
  profilePath?: string;
}

const ProductSellerCompanySummary = ({
  card,
  profilePath,
}: ProductSellerCompanySummaryProps) => {
  const summaryHeader = (
    <div className="flex h-[60px] items-center gap-[15px] md:h-[80px]">
      <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#eef5fa] text-base font-bold text-white transition-shadow group-hover:shadow-[0_0_0_2px_#1B4B86] md:h-[80px] md:w-[80px] md:text-[20px]">
        {card.logo_url ? (
          <img
            src={getPublicAssetUrl(card.logo_url)}
            alt={
              card.company_name ? `${card.company_name} logo` : "Company logo"
            }
            className="h-full w-full object-cover"
          />
        ) : (
          <IconCorporateFareBlue
            aria-hidden="true"
            className="h-[30px] w-[30px] text-[#1b4b86] md:h-[40px] md:w-[40px]"
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-[10px]">
          <h3
            title={card.company_name ?? "-"}
            className="min-w-0 truncate text-base font-bold text-black transition-colors group-hover:text-primary group-hover:underline group-hover:decoration-primary group-hover:underline-offset-4 md:text-[20px]"
          >
            {card.company_name ?? "-"}
          </h3>
          <span className="shrink-0 bg-[linear-gradient(90deg,#d8ff36_0%,#2cdcec_100%)] px-2 py-[3px] text-[11px] font-bold leading-none text-white md:text-[13px]">
            Lv.{card.current_level ?? "-"}
          </span>
        </div>
        <p className="mt-[3px] text-[11px] text-black md:text-[13px]">
          企業アカウント
        </p>
      </div>
    </div>
  );

  return (
    <div className="flex min-w-0 flex-col">
      {profilePath ? (
        <Link
          to={profilePath}
          className="group inline-flex max-w-full self-start text-inherit focus:outline-none focus-visible:rounded-[6px] focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          aria-label={`${card.company_name ?? "売り手企業"}の公開プロフィールを見る`}
        >
          {summaryHeader}
        </Link>
      ) : (
        summaryHeader
      )}

      <div className="mt-[15px] hidden sm:block">
        <ProductSellerBadgeList badges={card.badges} />
      </div>

      <dl className="mt-[15px] text-[15px] leading-[160%] text-black md:text-base">
        <div className="grid min-h-[54px] grid-cols-[90px_minmax(0,1fr)] items-center gap-[15px] border-b border-[#d5d5d5] py-[15px] sm:min-h-[56px] sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-0">
          <dt className="font-bold">企業名</dt>
          <dd>{card.company_name ?? "-"}</dd>
        </div>
        <div className="grid min-h-[78px] grid-cols-[90px_minmax(0,1fr)] items-center gap-[15px] border-b border-[#d5d5d5] py-[15px] sm:min-h-[82px] sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-0">
          <dt className="font-bold">所在地</dt>
          <dd>
            {card.postal_code && (
              <span className="block">〒{card.postal_code}</span>
            )}
            <span>{card.address ?? "-"}</span>
          </dd>
        </div>
        <div className="grid min-h-[54px] grid-cols-[90px_minmax(0,1fr)] items-center gap-[15px] border-b border-[#d5d5d5] py-[15px] sm:min-h-[56px] sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-0">
          <dt className="font-bold">過去の取引数</dt>
          <dd>{formatCount(card.total_deals)}件</dd>
        </div>
      </dl>

      <div className="mt-5 h-[74px] border-2 border-primary p-[5px] md:h-[90px]">
        <div className="flex h-full items-center justify-center gap-[15px] rounded-[4px] bg-[linear-gradient(90deg,#d8ff36_0%,#f4ffd1_50%,#d8ff36_100%)] px-[15px]">
          <span className="text-[14px] font-bold text-primary">構造スコア</span>
          <span className="font-['Audiowide'] text-[44px] leading-none text-primary md:text-[50px]">
            {card.total_xp === null ? "N/A" : formatCount(card.total_xp)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProductSellerCompanySummary;
